import { InformationType, POPULATION_TYPE } from "@/common/models";
import { SharedState } from "@/common/state";
import { reduce_and_merge_by_fn } from "@/fp";
import { cord, formatInput, gql2 } from "@/gql/lib";
import {
  ActionEnum,
  AxisType,
  QueryTier1countArgs,
  TimeAxisRangeInput,
} from "@/gql/types";
import { GqlCallsKey } from "@/mock/demo/gql.resolver";
import { BaseDashboardItem } from "../data/models";

const mapper_count_reducer = reduce_and_merge_by_fn<BaseDashboardItem, "id">(
  "id",
  (a, b) => {
    return {
      ...a,
      // @ts-ignore
      labels: [...a.labels, ...b.labels],
      count: a.count + b.count,
    };
  }
);

const create_query_input = (
  entity_type: AxisType,
  labels: string[],
  frame: TimeAxisRangeInput
): QueryTier1countArgs => {
  return {
    params: {
      coordinate: {
        x: cord(entity_type),
        y: {
          type: AxisType.ContentLabel,
          values: labels,
        },
        z: frame,
      },
      action: ActionEnum.List,
    },
  };
};

const count_per_entity_type = (
  call_tag: GqlCallsKey,
  population: POPULATION_TYPE,
  entity_type: AxisType
) =>
  gql2<{ is_virtual: boolean; labels: string[] }, BaseDashboardItem[]>(
    call_tag,
    ({ labels, is_virtual }, { frame, allInfoTypes }) => {
      return {
        query: `
        {
            total: tier1count(params:${formatInput(
              create_query_input(entity_type, labels, frame).params
            )}){
              y{value}
              count
              uniqs
            }
        }
        `,
        mapper: (res) => {
          const { total } = res;
          return mapper_count_reducer(
            total.map((item: any) => {
              const { y, uniqs } = item;
              let label: InformationType;
              if (is_virtual) {
                label = SharedState.virtualLabels.value.find(
                  (d) => d.id === y.value
                ) as InformationType;
              } else {
                label = SharedState.get_internal_external_label_by_id(y.value);
              }
              const parent_label = allInfoTypes[label.parentId as string];
              return {
                id: parent_label.id,
                name: parent_label.labelName,
                count: uniqs,
                labels: [y.value],
                axis_type: entity_type,
                type: population,
              };
            })
          );
        },
      };
    }
  );

export const from_cloud_folders = count_per_entity_type(
  "where.from-cloud-folders",
  POPULATION_TYPE.FOLDER,
  AxisType.FolderType
);
export const from_employees = count_per_entity_type(
  "where.from-employees",
  POPULATION_TYPE.EMPLOYEE,
  AxisType.EmployeeSenderType
);

export const to_employees = count_per_entity_type(
  "where.to-employees",
  POPULATION_TYPE.EMPLOYEE,
  AxisType.EmployeeReceiverType
);

export const to_external = count_per_entity_type(
  "where.to-external",
  POPULATION_TYPE.CONTACT,
  AxisType.ExternalReceiverType
);

export const to_organization = count_per_entity_type(
  "where.to-organizations",
  POPULATION_TYPE.ORGANIZATION,
  AxisType.OrganizationReceiverType
);

export const used_data_sources = gql2<
  {
    is_virtual: boolean;
    labels: string[];
  },
  any
>("mapping-sources.get", ({ is_virtual, labels }, { frame }) => {
  const q: QueryTier1countArgs = {
    params: {
      coordinate: {
        x: cord(AxisType.Application),
        y: {
          type: AxisType.ContentLabel,
          values: labels,
        },
        z: frame,
      },
      action: ActionEnum.List,
    },
  };
  return {
    query: `{
      total: tier1count(params:${formatInput(q.params)}){
        x{value}
        y{value}
        count
      }
    }`,
    mapper: (res) => {
      const { total } = res;
      return mapper_count_reducer(
        total.map((item: any) => {
          const { y, count, x } = item;
          const service = x.value;
          return {
            id: service,
            name: service,
            count: count,
            trend: 0,
            labels: [y.value],
            axis_type: AxisType.Application,
          };
        })
      ).reduce(
        (acc, item) => {
          acc[item.id] = item;
          return acc;
        },
        SharedState.data_sources.value.reduce((f, { id }) => {
          f[id] = { count: 0 };
          return f;
        }, {} as Record<string, { count: number }>)
      );
    },
  };
});
